import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom"; // at the top of your file
import api from "../services/api";
import "./AllBusinessesPage.css";

const AllBusinessesPage = () => {
  const [businesses, setBusinesses] = useState([]);
  const [accountsByBusiness, setAccountsByBusiness] = useState({});
  const [selectedAccounts, setSelectedAccounts] = useState({});
  const [billingSetupByBusiness, setBillingSetupByBusiness] = useState({});
  const [showPopupFor, setShowPopupFor] = useState(null);
  const [showChangeFor, setShowChangeFor] = useState(null);
  const [changeAccountSelection, setChangeAccountSelection] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: bizData } = await api.get("/businesses");
        setBusinesses(bizData);

        const { data: billingMap } = await api.get(`/billing/funding-accounts`);
        setBillingSetupByBusiness(billingMap);

        for (const biz of bizData) {
          try {
            const { data: linked } = await api.get(`/plaid/by-pay/${biz.id}`);
            const eligible = linked.filter(
              (a) => a.subtype === "checking" || a.subtype === "savings"
            );
            setAccountsByBusiness((prev) => ({
              ...prev,
              [biz.id]: eligible,
            }));
          } catch (err) {
            console.error(`❌ Error fetching accounts for ${biz.id}:`, err);
          }
        }
      } catch (err) {
        console.error("❌ Failed to fetch businesses or billing info:", err);
      }
    };

    fetchData();
  }, []);

  const handleAccountChange = (businessId, accountId) => {
    setSelectedAccounts((prev) => ({
      ...prev,
      [businessId]: accountId,
    }));
  };

  const handleBilling = (businessId, overrideAccountId) => {
    const accountId = overrideAccountId || selectedAccounts[businessId];
    if (!accountId) return alert("Please select an account for this business.");

    api.post("/plaid/billing/setup", {
        businessId,
        account_id: accountId,
        amount: 50.0,
        description: "Payweek monthly subscription",
      })
      .then(() => {
        alert("Billing info saved successfully.");
        setBillingSetupByBusiness((prev) => ({
          ...prev,
          [businessId]: {
            account_id: accountId,
            amount: 50.0,
            description: "Payweek monthly subscription",
            ...accountsByBusiness[businessId].find((a) => a.account_id === accountId),
          },
        }));
        setShowChangeFor(null);
      })
      .catch((err) => {
        console.error("❌ Failed to save billing info:", err);
        alert("Failed to save billing info. See console.");
      });
  };

  const handleAddFundingAccount = async (businessId) => {
    try {
      const res = await api.post("/plaid/create_funding_link_token", { businessId });
      const linkToken = res.data.link_token;

      const handler = window.Plaid.create({
        token: linkToken,
        onSuccess: async (public_token) => {
          await api.post("/plaid/exchange_funding_token", {
            public_token,
            businessId,
          });

          const updated = await api.get(`/plaid/by-pay/${businessId}`);
          const filtered = updated.data.filter(
            (a) => a.subtype === "checking" || a.subtype === "savings"
          );
          setAccountsByBusiness((prev) => ({
            ...prev,
            [businessId]: filtered,
          }));

          alert("Funding account added successfully!");
        },
      });

      handler.open();
    } catch (err) {
      console.error("❌ Error linking funding account:", err);
      alert("Could not add funding account.");
    }
  };

  return (
    <div className="billing-page">
      <h3>Billing</h3>
      <div className="billing-grid">
        {businesses.map((biz) => {
          const billingAccount = billingSetupByBusiness[biz.id];

          return (
            <div key={biz.id} className="billing-card">
              <h3>{biz.name}</h3>

              {billingAccount ? (
                <div className="funding-account-display">
                  <p>
                    Using <strong>{billingAccount.subtype}</strong> ••••
                    {billingAccount.mask} for billing.
                  </p>
                  
                  <button onClick={() => setShowChangeFor(biz)}>
                    Change Billing Account
                  </button>
                </div>
              ) : (
                <>
                 
                  <select
                    value={selectedAccounts[biz.id] || ""}
                    onChange={(e) => handleAccountChange(biz.id, e.target.value)}
                  >
                    <option value="">-- Select Account --</option>
                    {(accountsByBusiness[biz.id] || []).map((acct) => (
                      <option key={acct.account_id} value={acct.account_id}>
                        {acct.subtype} ••••{acct.mask}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleBilling(biz.id)}
                    disabled={!selectedAccounts[biz.id]}
                  >
                    Bill This Account
                  </button>
                  <span
                      onClick={() => setShowPopupFor(biz)}
                      style={{
                        color: "#2c7be5",
                        textDecoration: "underline",
                        cursor: "pointer",
                        display: "inline-block",
                        marginTop: "0.5rem",
                        fontSize: "13px"
                      }}
                    >
                      + Add Funding Account
                    </span>

                  
                </>
              )}
            </div>
          );
        })}
      </div>

      {showPopupFor && (
        <div className="popup-overlay-billing">
          <div className="popup-content-billing">
            <h4>Link a Funding Account</h4>
            <p>
              If you would like to fetch transactions from this account, please add this account on the{" "}
              <Link
                to={`/dashboard/business/${showPopupFor.id}/accounting`}
                style={{ color: "#2c7be5", textDecoration: "underline" }}
              >
                Accounting Page
              </Link>

            </p>
            <div className="popup-buttons-billing">
              <button
                onClick={() => {
                  handleAddFundingAccount(showPopupFor.id);
                  setShowPopupFor(null);
                }}
              >
                Add Funding Account
              </button>
              <button className="cancel-button" onClick={() => setShowPopupFor(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showChangeFor && (
        <div className="popup-overlay-billing">
          <div className="popup-content-billing">
            <h4>Change Billing Account</h4>
            <label>Select an Account:</label>
            <select
              value={changeAccountSelection[showChangeFor.id] || ""}
              onChange={(e) =>
                setChangeAccountSelection((prev) => ({
                  ...prev,
                  [showChangeFor.id]: e.target.value,
                }))
              }
            >
              <option value="">-- Select Account --</option>
              {(accountsByBusiness[showChangeFor.id] || []).map((acct) => (
                <option key={acct.account_id} value={acct.account_id}>
                  {acct.subtype} ••••{acct.mask}
                </option>
              ))}
            </select>

            <p style={{ marginTop: "1rem" }}>
              Need to connect a new account?{" "}
              <span
                onClick={() => {
                  setShowPopupFor(showChangeFor);
                  setShowChangeFor(null);
                }}
                style={{ color: "#2c7be5", textDecoration: "underline", cursor: "pointer" }}
              >
                + Add Funding Account
              </span>
            </p>

            <div className="popup-buttons-billing">
              <button
                onClick={() => {
                  const selected = changeAccountSelection[showChangeFor.id];
                  if (!selected) return alert("Please select an account.");
                  handleBilling(showChangeFor.id, selected);
                }}
              >
                Save Change
              </button>
              <button className="cancel-button" onClick={() => setShowChangeFor(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllBusinessesPage;




// import React, { useEffect, useState } from "react";
// import api from "../services/api";
// import "./AllBusinessesPage.css";

// const AllBusinessesPage = () => {
//   const [businesses, setBusinesses] = useState([]);
//   const [accountsByBusiness, setAccountsByBusiness] = useState({});
//   const [selectedAccounts, setSelectedAccounts] = useState({});
//   const [billingSetupByBusiness, setBillingSetupByBusiness] = useState({});
//   const [showPopupFor, setShowPopupFor] = useState(null);
//   const [showChangeFor, setShowChangeFor] = useState(null); // holds the business object
//   const [changeAccountSelection, setChangeAccountSelection] = useState({});


//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const { data: bizData } = await api.get("/businesses");
//         setBusinesses(bizData);

//         for (const biz of bizData) {
//           try {
//             // Get eligible linked accounts
//             const { data: linked } = await api.get(`/plaid/by-pay/${biz.id}`);
//             const eligible = linked.filter(
//               (a) => a.subtype === "checking" || a.subtype === "savings"
//             );
//             setAccountsByBusiness((prev) => ({
//               ...prev,
//               [biz.id]: eligible,
//             }));

//             // Get saved billing info (if any)
//             const { data: billingMap } = await api.get(`/billing/funding-accounts`);
//              setBillingSetupByBusiness(billingMap);

            
//           } catch (err) {
//             console.error(`❌ Error loading info for business ${biz.id}:`, err);
//           }
//         }
//       } catch (err) {
//         console.error("❌ Failed to fetch businesses:", err);
//       }
//     };

//     fetchData();
//   }, []);

//   const handleAccountChange = (businessId, accountId) => {
//     setSelectedAccounts((prev) => ({
//       ...prev,
//       [businessId]: accountId,
//     }));
//   };

//   const handleBilling = (businessId) => {
//     const accountId = selectedAccounts[businessId];
//     if (!accountId) return alert("Please select an account for this business.");

//     api.post("/plaid/billing/setup", {
//       businessId,
//       account_id: accountId,
//       amount: 50.0,
//       description: "Payweek monthly subscription",
//     })
//       .then(() => {
//         alert("Billing info saved successfully.");
//         // Optionally refresh billing setup
//         setBillingSetupByBusiness((prev) => ({
//           ...prev,
//           [businessId]: {
//             account_id: accountId,
//             amount: 50.0,
//             description: "Payweek monthly subscription",
//             ...accountsByBusiness[businessId].find((a) => a.account_id === accountId),
//           },
//         }));
//       })
//       .catch((err) => {
//         console.error("❌ Failed to save billing info:", err);
//         alert("Failed to save billing info. See console.");
//       });
//   };

//   const handleAddFundingAccount = async (businessId) => {
//     try {
//       const res = await api.post("/plaid/create_funding_link_token", { businessId });
//       const linkToken = res.data.link_token;

//       const handler = window.Plaid.create({
//         token: linkToken,
//         onSuccess: async (public_token) => {
//           await api.post("/plaid/exchange_funding_token", {
//             public_token,
//             businessId,
//           });

//           const updated = await api.get(`/plaid/by-pay/${businessId}`);
//           const filtered = updated.data.filter(
//             (a) => a.subtype === "checking" || a.subtype === "savings"
//           );
//           setAccountsByBusiness((prev) => ({
//             ...prev,
//             [businessId]: filtered,
//           }));

//           alert("Funding account added successfully!");
//         },
//       });

//       handler.open();
//     } catch (err) {
//       console.error("❌ Error linking funding account:", err);
//       alert("Could not add funding account.");
//     }
//   };

//   return (
//     <div className="billing-page">
//       <h2>Select Billing Account by Business</h2>
//       <div className="billing-grid">
//         {businesses.map((biz) => {
//           const billingAccount = billingSetupByBusiness[biz.id];

//           return (
//             <div key={biz.id} className="billing-card">
//               <h3>{biz.name}</h3>

//               {billingAccount ? (
//                 <div className="funding-account-display">
//                   <p>
//                     Using <strong>{billingAccount.subtype}</strong> ••••
//                     {billingAccount.mask} for billing.
//                   </p>
//                   <p>
                   
//                   </p>
//                 </div>
//               ) : (
//                 <>
//                   <label>Select Bank Account:</label>
//                   <select
//                     value={selectedAccounts[biz.id] || ""}
//                     onChange={(e) => handleAccountChange(biz.id, e.target.value)}
//                   >
//                     <option value="">-- Select Account --</option>
//                     {(accountsByBusiness[biz.id] || []).map((acct) => (
//                       <option key={acct.account_id} value={acct.account_id}>
//                         {acct.subtype} ••••{acct.mask}
//                       </option>
//                     ))}
//                   </select>
//                   <button onClick={() => setShowPopupFor(biz)}>
//                     + Add Funding Account
//                   </button>
//                 </>
//               )}

//               {!billingAccount && (
//                 <button
//                   onClick={() => handleBilling(biz.id)}
//                   disabled={!selectedAccounts[biz.id]}
//                 >
//                   Bill This Account
//                 </button>
//               )}
//             </div>
//           );
//         })}
//       </div>

//       {showPopupFor && (
//         <div className="popup-overlay">
//           <div className="popup-content">
//             <h4>Link a Funding Account</h4>
//             <p>
//               If you would like to fetch transactions from this account, please add this account on the{" "}
//               <a
//                 href={`/dashboard/business/${showPopupFor.id}/accounting`}
//                 style={{ color: "#2c7be5", textDecoration: "underline" }}
//               >
//                 Accounting Page
//               </a>.
//             </p>
//             <div className="popup-buttons">
//               <button
//                 onClick={() => {
//                   handleAddFundingAccount(showPopupFor.id);
//                   setShowPopupFor(null);
//                 }}
//               >
//                 Add Funding Account
//               </button>
//               <button className="cancel-button" onClick={() => setShowPopupFor(null)}>
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default AllBusinessesPage;




// import React, { useEffect, useState } from "react";
// //import { useNavigate } from "react-router-dom";
// import api from "../services/api";
// import "./AllBusinessesPage.css";

// const AllBusinessesPage = () => {
//   const [businesses, setBusinesses] = useState([]);
//   const [accountsByBusiness, setAccountsByBusiness] = useState({});
//   const [selectedAccounts, setSelectedAccounts] = useState({});
//   const [showPopupFor, setShowPopupFor] = useState(null); // holds the business object or null

//   const [fundingAccounts, setFundingAccounts] = useState({});


//   //const navigate = useNavigate();

//   useEffect(() => {
//     const fetchBusinessesAndAccounts = async () => {
//       try {
//         const { data } = await api.get("/businesses");
//         setBusinesses(data);

//         for (const biz of data) {
//           try {
//             const res = await api.get(`/plaid/by-pay/${biz.id}`);
//             const eligible = res.data.filter(
//               (a) => a.subtype === "checking" || a.subtype === "savings"
//             );
//             setAccountsByBusiness((prev) => ({
//               ...prev,
//               [biz.id]: eligible,
//             }));

//             const fundRes = await api.get(`/plaid/funding-account/${biz.id}`);
//                 if (fundRes.data) {
//                   setFundingAccounts((prev) => ({
//                     ...prev,
//                     [biz.id]: fundRes.data,
//                   }));
//                 }

//           } catch (err) {
//             console.error(`❌ Failed to fetch accounts for business ${biz.id}:`, err);
//           }
//         }
//       } catch (err) {
//         console.error("❌ Failed to fetch businesses:", err);
//       }
//     };

//     fetchBusinessesAndAccounts();
//   }, []);

//   const handleAccountChange = (businessId, accountId) => {
//     setSelectedAccounts((prev) => ({
//       ...prev,
//       [businessId]: accountId,
//     }));
//   };

//   const handleBilling = (businessId) => {
//   const accountId = selectedAccounts[businessId];
//   if (!accountId) return alert("Please select an account for this business.");

//   api.post("/plaid/billing/setup", {
//     businessId,
//     account_id: accountId,
//     amount: 50.0,
//     description: "Payweek monthly subscription"
//   })
//     .then(() => alert("Billing info saved successfully."))
//     .catch((err) => {
//       console.error("❌ Failed to save billing info:", err);
//       alert("Failed to save billing info. See console.");
//     });
// };



//   const handleAddFundingAccount = async (businessId) => {
//   try {
//     const res = await api.post("/plaid/create_funding_link_token", { businessId });
//     const linkToken = res.data.link_token;

//     const handler = window.Plaid.create({
//       token: linkToken,
//       onSuccess: async (public_token) => {
//         await api.post("/plaid/exchange_funding_token", {
//           public_token,
//           businessId,
//         });

//         // After linking, refresh this business's account list only
//         const updated = await api.get(`/plaid/by-accounts/${businessId}`);
//         const filtered = updated.data.filter(
//           (a) => a.subtype === "checking" || a.subtype === "savings"
//         );

//         setAccountsByBusiness((prev) => ({
//           ...prev,
//           [businessId]: filtered,
//         }));

//         alert("Funding account added successfully!");
//       },
//     });

//     handler.open();
//   } catch (err) {
//     console.error("❌ Error linking funding account:", err);
//     alert("Could not add funding account.");
//   }
// };


//   return (
//     <div className="billing-page">
//       <h2>Select Billing Account by Business</h2>
//       <div className="billing-grid">
//         {businesses.map((biz) => (
//           <div key={biz.id} className="billing-card">
//             <h3>{biz.name}</h3>
            

//             <label>Select Bank Account:</label>
//             <select
//               value={selectedAccounts[biz.id] || ""}
//               onChange={(e) => handleAccountChange(biz.id, e.target.value)}
//             >
//               <option value="">-- Select Account --</option>
//               {(accountsByBusiness[biz.id] || []).map((acct) => (
//                 <option key={acct.account_id} value={acct.account_id}>
//                   {acct.subtype} ••••{acct.mask}
//                 </option>
//               ))}
//             </select>
//             <button onClick={() => setShowPopupFor(biz)}>
//               + Add Funding Account
//             </button>


//             <button onClick={() => handleBilling(biz.id)}>
//               Bill This Account
//             </button>
//           </div>
//         ))}
//       </div>
//       {showPopupFor && (
//   <div className="popup-overlay">
//     <div className="popup-content">
//       <h4>Link a Funding Account</h4>
//       <p>
//         If you would like to fetch transactions from this account, please add this account on the{" "}
//         <a
//           href={`/dashboard/business/${showPopupFor.id}/accounting`}
//           style={{ color: "#2c7be5", textDecoration: "underline" }}
//         >
//           Accounting Page
//         </a>.
//       </p>
//       <div className="popup-buttons">
//         <button
//           onClick={() => {
//             handleAddFundingAccount(showPopupFor.id);
//             setShowPopupFor(null);
//           }}
//         >
//           Add Funding Account
//         </button>
//         <button className="cancel-button" onClick={() => setShowPopupFor(null)}>
//           Cancel
//         </button>
//       </div>
//     </div>
//   </div>
// )}

//     </div>
//   );
// };

// export default AllBusinessesPage;
